import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Bell,
    Calendar,
    Plus,
    X,
    Users,
    Mail,
    CalendarClock,
    Save,
    Trash2,
    Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import api from '../../utils/api';
import { toast } from 'sonner';
import { toSydneyDate } from '../../utils/helpers';
import { Asset, ReminderEventType, ReminderRecurrencePattern, ReminderStatus, AssetReminder } from '../../types/asset';
import { User } from '../../types';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

const EVENT_TYPES: ReminderEventType[] = ['Registration', 'Service', 'Insurance', 'Inspection', 'License', 'Calibration', 'Other'];
const RECURRENCE_PATTERNS: ReminderRecurrencePattern[] = ['One-time', 'Monthly', 'Quarterly', 'Every 6 Months', 'Annually', 'Every 2 Years', 'Custom'];
const STATUSES: ReminderStatus[] = ['Active', 'Inactive', 'Completed'];

const EditReminderPage: React.FC = () => {
    const { id, reminderId } = useParams<{ id: string, reminderId: string }>();
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);

    const [asset, setAsset] = useState<Asset | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Event Details
    const [eventName, setEventName] = useState('');
    const [eventType, setEventType] = useState<ReminderEventType>('Service');
    const [description, setDescription] = useState('');

    // Schedule
    const [firstOccurrenceDate, setFirstOccurrenceDate] = useState('');
    const [recurrencePattern, setRecurrencePattern] = useState<ReminderRecurrencePattern>('Annually');
    const [customIntervalDays, setCustomIntervalDays] = useState('');
    const [reminderIntervals, setReminderIntervals] = useState<number[]>([14]);
    const [newInterval, setNewInterval] = useState('7');
    const [status, setStatus] = useState<ReminderStatus>('Active');

    // Notifications
    const [notifyAssignee, setNotifyAssignee] = useState(true);
    const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>([]);
    const [emailTemplate, setEmailTemplate] = useState('');
    const [userSearchTerm, setUserSearchTerm] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [assetRes, usersRes, remindersRes] = await Promise.all([
                    api.get<Asset>(`/api/assets/${id}`),
                    api.get<{ users: User[] }>('/api/users?all_users=true'),
                    api.get<AssetReminder[]>(`/api/assets/${id}/reminders`)
                ]);

                setAsset(assetRes);
                setUsers(Array.isArray(usersRes.users) ? usersRes.users : []);

                const reminder = remindersRes.find(r => r.id.toString() === reminderId);
                if (reminder) {
                    setEventName(reminder.eventName);
                    setEventType(reminder.eventType);
                    setDescription(reminder.description || '');
                    setFirstOccurrenceDate(reminder.firstOccurrenceDate.split('T')[0]);
                    setRecurrencePattern(reminder.recurrencePattern);
                    setCustomIntervalDays(reminder.customIntervalDays?.toString() || '');
                    setReminderIntervals(reminder.reminderIntervals || []);
                    setStatus(reminder.status);
                    setNotifyAssignee(reminder.notifyAssignee);
                    setEmailTemplate(reminder.emailTemplate || '');
                    setSelectedRecipientIds(reminder.recipients?.map(r => r.user?.id?.toString()).filter(Boolean) as string[] || []);
                } else {
                    toast.error('Reminder not found');
                    navigate(`/assets/${id}`);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load reminder data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, reminderId, navigate]);

    if (!ability.can(ACTIONS.UPDATE, RESOURCES.ASSET)) {
        return <AccessRestricted />;
    }

    const addInterval = () => {
        const days = parseInt(newInterval);
        if (isNaN(days) || days <= 0) {
            toast.error('Please enter a valid number of days');
            return;
        }
        if (reminderIntervals.includes(days)) {
            toast.error('This interval already exists');
            return;
        }
        setReminderIntervals(prev => [...prev, days].sort((a, b) => b - a));
        setNewInterval('');
    };

    const removeInterval = (days: number) => {
        setReminderIntervals(prev => prev.filter(d => d !== days));
    };

    const toggleRecipient = (userId: string | number) => {
        const userIdStr = userId.toString();
        setSelectedRecipientIds(prev =>
            prev.includes(userIdStr)
                ? prev.filter(id => id !== userIdStr)
                : [...prev, userIdStr]
        );
    };

    const filteredUsers = users.filter(user => {
        const name = (user.name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const jobRole = (user.jobRole || '').toLowerCase();
        const term = (userSearchTerm || '').toLowerCase();

        return name.includes(term) || email.includes(term) || jobRole.includes(term);
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!eventName.trim()) {
            toast.error('Event name is required');
            return;
        }
        if (!firstOccurrenceDate) {
            toast.error('First occurrence date is required');
            return;
        }
        if (recurrencePattern === 'Custom' && (!customIntervalDays || parseInt(customIntervalDays) <= 0)) {
            toast.error('Please enter a valid custom interval in days');
            return;
        }

        try {
            setSubmitting(true);
            await api.patch(`/api/assets/${id}/reminders/${reminderId}`, {
                eventName: eventName.trim(),
                eventType,
                description: description.trim() || null,
                firstOccurrenceDate,
                recurrencePattern,
                customIntervalDays: recurrencePattern === 'Custom' ? parseInt(customIntervalDays) : null,
                reminderIntervals,
                status,
                notifyAssignee,
                emailTemplate: emailTemplate.trim() || null,
                recipientIds: Array.from(new Set(selectedRecipientIds.map(id => parseInt(id)))),
            });

            toast.success('Reminder updated successfully');
            navigate(`/assets/${id}?tab=reminders`);
        } catch (error: any) {
            console.error('Error updating reminder:', error);
            toast.error(error.response?.data?.message || 'Failed to update reminder');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-6 mb-8">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 flex-shrink-0"
                    onClick={() => navigate(-1)}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Edit Reminder</h1>
                    <p className="text-gray-500 text-sm font-medium mt-1">
                        Modify scheduled notifications for <span className="text-gray-900 font-bold">{asset?.name} ({asset?.tag})</span>
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col xl:flex-row gap-6">
                {/* Main Content Area */}
                <div className="flex-1 space-y-6">
                    {/* Event Details */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3">
                            <CalendarClock className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Event Details</h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">What is this reminder for?</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                    Event Name <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="e.g., Annual Registration"
                                    value={eventName}
                                    onChange={e => setEventName(e.target.value)}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                    Event Type <span className="text-red-500">*</span>
                                </label>
                                <Select value={eventType} onValueChange={(val: any) => setEventType(val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold px-4">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl">
                                        {EVENT_TYPES.map(t => (
                                            <SelectItem key={t} value={t} className="font-medium">{t}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide text-amber-600">Additional Notes</label>
                            <Textarea
                                placeholder="Describe the event or provide instructions..."
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[100px] p-4 resize-none"
                            />
                        </div>
                    </div>

                    {/* Schedule */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3">
                            <Calendar className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Schedule</h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">When should notifications be sent?</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                    First Occurrence Date <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="date"
                                    max="9999-12-31"
                                    value={firstOccurrenceDate}
                                    onChange={e => setFirstOccurrenceDate(e.target.value)}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                    Recurrence Pattern <span className="text-red-500">*</span>
                                </label>
                                <Select value={recurrencePattern} onValueChange={(val: any) => setRecurrencePattern(val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold px-4">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl">
                                        {RECURRENCE_PATTERNS.map(p => (
                                            <SelectItem key={p} value={p} className="font-medium">{p}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {recurrencePattern === 'Custom' && (
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                    Custom Interval (Days) <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="number"
                                    min="1"
                                    placeholder="e.g., 90"
                                    value={customIntervalDays}
                                    onChange={e => setCustomIntervalDays(e.target.value)}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4 w-48"
                                />
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Reminder Intervals</label>
                                    <p className="text-[10px] font-medium text-gray-400 mt-0.5">Define multiple notification points before the due date</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {reminderIntervals.map(days => (
                                    <div
                                        key={days}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50/50 text-blue-700 text-xs font-bold rounded-xl border border-blue-100 shadow-sm animate-in zoom-in duration-300"
                                    >
                                        <Bell size={12} className="text-blue-400" />
                                        {days} days before
                                        <button
                                            type="button"
                                            onClick={() => removeInterval(days)}
                                            className="ml-1 text-blue-300 hover:text-red-500 transition-colors"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                                {reminderIntervals.length === 0 && (
                                    <p className="text-xs italic text-gray-400 font-medium">No early reminders set. Notification will only be sent on the due date.</p>
                                )}
                            </div>

                            <div className="flex items-end gap-3 p-4 bg-gray-50/50 rounded-2xl border border-gray-100 border-dashed">
                                <div className="space-y-1.5 flex-1 max-w-[200px]">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Days Before Due</label>
                                    <Input
                                        type="number"
                                        min="1"
                                        placeholder="7"
                                        value={newInterval}
                                        onChange={e => setNewInterval(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addInterval(); } }}
                                        className="rounded-xl h-10 px-4 bg-white border-gray-200"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={addInterval}
                                    className="h-10 rounded-xl border-gray-200 bg-white font-bold text-xs gap-1.5 px-4 hover:bg-gray-50 shadow-sm"
                                >
                                    <Plus size={14} />
                                    Add Interval
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Notifications */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3">
                            <Users className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Recipient Group</h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">Who should receive these alerts?</p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Checkbox
                                        id="notify-assignee"
                                        checked={notifyAssignee}
                                        onCheckedChange={(checked) => setNotifyAssignee(checked === true)}
                                        className="h-5 w-5 rounded-md"
                                    />
                                    <label htmlFor="notify-assignee" className="text-sm font-bold text-gray-700 cursor-pointer">
                                        Notify current asset assignee
                                    </label>
                                </div>
                                <span className="text-[10px] font-bold text-blue-500 bg-blue-50 px-2 py-0.5 rounded-lg uppercase tracking-wider">Recommended</span>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Additional Recipients</label>
                                    <div className="relative w-full max-w-[240px]">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                        <Input
                                            type="text"
                                            placeholder="Search users..."
                                            value={userSearchTerm}
                                            onChange={(e) => setUserSearchTerm(e.target.value)}
                                            className="h-9 pl-9 rounded-xl border-gray-100 bg-gray-50/50 text-xs focus-visible:ring-blue-500"
                                        />
                                    </div>
                                </div>
                                <div className="border border-gray-100 rounded-3xl overflow-hidden bg-gray-50/50">
                                    <div className="max-h-[300px] overflow-y-auto divide-y divide-gray-100/50">
                                        {filteredUsers.length === 0 ? (
                                            <div className="p-10 text-center">
                                                <Users size={24} className="mx-auto text-gray-300 mb-2" />
                                                <p className="text-sm text-gray-400 font-medium">
                                                    {userSearchTerm ? `No users matching "${userSearchTerm}"` : 'No users available'}
                                                </p>
                                            </div>
                                        ) : (
                                            filteredUsers.map(user => (
                                                <label
                                                    key={user.id}
                                                    className={`flex items-center gap-4 px-6 py-3.5 cursor-pointer transition-all hover:bg-white group ${selectedRecipientIds.includes(user.id.toString()) ? 'bg-blue-50/30' : ''}`}
                                                >
                                                    <Checkbox
                                                        checked={selectedRecipientIds.includes(user.id.toString())}
                                                        onCheckedChange={() => toggleRecipient(user.id)}
                                                        className="h-5 w-5 rounded-md"
                                                    />
                                                    <div className="flex-1">
                                                        <p className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{user.name}</p>
                                                        <p className="text-xs text-gray-500">{user.email}</p>
                                                    </div>
                                                    <span className="text-[10px] font-bold text-gray-400 bg-gray-100/50 px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                                                        {user.jobRole || 'Employee'}
                                                    </span>
                                                </label>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4 pt-4 border-t border-gray-50">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                                    <Mail size={14} className="text-gray-400" />
                                    Custom Email Message
                                </label>
                                <Textarea
                                    placeholder="Add a personalized message that will be included in the notification emails..."
                                    value={emailTemplate}
                                    onChange={e => setEmailTemplate(e.target.value)}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[120px] p-4 resize-none"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar Sticky Summary area */}
                <div className="w-full xl:w-[350px] flex-shrink-0">
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm sticky top-6 space-y-8">
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <CalendarClock className="text-gray-400" size={20} />
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Summary</h2>
                            </div>

                            <div className="space-y-6">
                                <div className="space-y-1.5">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Reminder Event</span>
                                    <p className="text-sm font-bold text-gray-900 truncate">{eventName || 'Not specified'}</p>
                                    <p className="text-xs text-blue-600 font-bold">{eventType}</p>
                                </div>
                                <div className="space-y-1.5">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Next Occurrence</span>
                                    <p className="text-sm font-bold text-gray-900">
                                        {firstOccurrenceDate ? toSydneyDate(firstOccurrenceDate) : 'Date not set'}
                                    </p>
                                    <p className="text-xs text-gray-500 font-medium">Follows {recurrencePattern} pattern</p>
                                </div>
                                <div className="space-y-1.5">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Notifications</span>
                                    <p className="text-sm font-bold text-gray-900">
                                        {selectedRecipientIds.length + (notifyAssignee ? 1 : 0)} Total recipients
                                    </p>
                                    <p className="text-xs text-gray-500 font-medium">{reminderIntervals.length} sequence alerts</p>
                                </div>
                                <div className="space-y-1.5 pt-4 border-t border-gray-100">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Internal Status</span>
                                    <Select value={status} onValueChange={(val: any) => setStatus(val)}>
                                        <SelectTrigger className="h-9 rounded-xl text-xs font-bold border-gray-100 bg-gray-50">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl">
                                            {STATUSES.map(s => (
                                                <SelectItem key={s} value={s} className="text-xs font-bold">{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <Button
                                type="submit"
                                className="w-full rounded-2xl bg-gray-900 hover:bg-gray-800 text-white h-12 font-bold gap-2 text-sm shadow-md transition-all"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        Update Reminder
                                    </>
                                )}
                            </Button>

                            <Button
                                type="button"
                                variant="outline"
                                className="w-full rounded-2xl border-gray-100 hover:bg-red-50 hover:text-red-600 hover:border-red-100 h-10 font-bold transition-all text-xs"
                                onClick={() => navigate(-1)}
                            >
                                Discard Changes
                            </Button>
                        </div>

                        <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 border-dashed">
                            <p className="text-[10px] leading-relaxed text-amber-700 font-medium">
                                <strong>Tip:</strong> You can add multiple reminder points (e.g. 30, 15, and 7 days before) to ensure team members are well-informed as the date approaches.
                            </p>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default EditReminderPage;
